import {
  isColorValueCode,
  resolveColorHex
} from '../../../colors/color_catalog_registry.js?v=20260720a';
import { normalizeAngle, rotateWorldPoint, snap } from '../../plugin-sdk/geometry.js';

const NS = 'http://www.w3.org/2000/svg';
const DEFAULT_GRID_COLOR = 'RAL9018';
const CARDINALS = ['north','east','south','west'];
const PROJECT_LEVEL_KEYS = Object.freeze(['ub', 'db', 'rh', 'dp', 'dd']);
const PROJECT_LEVEL_FLOOR_GRID_SIZES = Object.freeze([600, 750]);
const PROJECT_LEVEL_DOUBLE_CEILING_GRIDS = Object.freeze({
  '600_white': { label: '600x600 weiss' },
  '600_gray': { label: '600x600 grau' }
});
const PROJECT_LEVEL_FLOOR_COLORS = Object.freeze({
  white: { label: 'weiss', color: '#f8fafc' },
  moss: { label: 'moosgrün', color: '#4b5d35' },
  gray: { label: 'grau', color: '#8b8f87' },
  brown: { label: 'braun', color: '#6b4f3a' }
});
const PROJECT_LEVEL_FLOOR_TYPES = Object.freeze({
  pavers: { label: 'Pflasterstein' },
  concrete_slabs: { label: 'Betonplatten' },
  pebbles: { label: 'Kieselsteine' },
  asphalt: { label: 'Asphalt' },
  stein1: { label: 'Stein 1' },
  stein2: { label: 'Stein 2' },
  stein3: { label: 'Stein 3' },
  concrete_floor: { label: 'Beton' }
});
const PROJECT_LEVEL_CEILING_TYPES = Object.freeze({
  spaghetti: { label: 'Spaghetti-Decke' },
  concrete: { label: 'Beton' },
  sky: { label: 'Himmel' },
  datacenter_technical: { label: 'technische Decke Datacenter' }
});
const PROJECT_ENVIRONMENT_PRESET_KEYS = Object.freeze(['custom', 'datacenter', 'basement', 'outdoor']);
const DEFAULT_PROJECT_LEVEL_OPTIONS = Object.freeze({
  ub: Object.freeze({ includeInCalculation: true, showIn3d: false }),
  db: Object.freeze({ includeInCalculation: true, showIn3d: false }),
  rh: Object.freeze({ includeInCalculation: true, showIn3d: false }),
  dp: Object.freeze({ includeInCalculation: true, showIn3d: false }),
  dd: Object.freeze({ includeInCalculation: true, showIn3d: false })
});
const DEFAULT_PROJECT_LEVELS = Object.freeze({
  ub: 600,
  db: 40,
  rh: 2500,
  dp: 30,
  dd: 300,
  options: DEFAULT_PROJECT_LEVEL_OPTIONS,
  floorGridSize: 600,
  floorColor: 'gray',
  doubleCeilingGrid: '600_white',
  floorType: 'concrete_floor',
  ceilingType: 'concrete',
  environmentPreset: 'basement',
  floorTexture: null,
  ceilingTexture: null,
  doubleFloorTexture: null,
  doubleCeilingTexture: null
});

const GC_GALVANIZED = '#C4C8C1';

/**
 * Beschreibt gc Parse im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} h - Eingabewert für gc Parse.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function _gcParse(h){ return [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)]; }
/**
 * Beschreibt gc Hex im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} arr - Eingabewert für gc Hex.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function _gcHex(arr){ return '#'+arr.map(v=>Math.max(0,Math.min(255,Math.round(v))).toString(16).padStart(2,'0')).join(''); }
/**
 * Beschreibt gc Fill im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} code - Eingabewert für gc Fill.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function gcFill(code){
  const gc = code || DEFAULT_GRID_COLOR;
  if (isColorValueCode(gc, 'galvanized')) return GC_GALVANIZED;
  return resolveColorHex(gc, '#CFCCC8');
}
/**
 * Beschreibt gc Stroke im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} code - Eingabewert für gc Stroke.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function gcStroke(code){
  const [r,g,b] = _gcParse(gcFill(code));
  return _gcHex([r*0.7,g*0.7,b*0.7]);
}
/**
 * Beschreibt gc Wire im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} code - Eingabewert für gc Wire.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function gcWire(code){
  const [r,g,b] = _gcParse(gcFill(code));
  return _gcHex([r*0.82,g*0.82,b*0.82]);
}
/**
 * Beschreibt uid im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} prefix - Eingabewert für uid.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function uid(prefix='id'){
  return `${prefix}_${Math.random().toString(36).slice(2,8)}_${Date.now().toString(36).slice(-4)}`;
}
/**
 * Beschreibt clamp im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} v - Eingabewert für clamp.
 * @param {*} min - Eingabewert für clamp.
 * @param {*} max - Eingabewert für clamp.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function clamp(v,min,max){ return Math.max(min, Math.min(max, v)); }
/**
 * Beschreibt snap im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Snap-Beziehungen, Fangpunkte und Persistenz zwischen Elementen, damit Aufrufer nicht die Moduldetails kennen müssen.
 * - Bewahrt die fachliche Beziehung zwischen Kontaktpunkten, Wandachsen und gespeicherten Snap-Daten.
 * @param {*} v - Eingabewert für snap.
 * @param {*} grid - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
/**
 * Normalisiert normalize Project Levels im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt State-Normalisierung, Migration und robuste Defaults, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} levels - Eingabewert für normalize Project Levels.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function normalizeProjectLevels(levels){
  const source = levels && typeof levels === 'object' ? levels : {};
  const sourceOptions = source.options && typeof source.options === 'object' ? source.options : {};
  const normalized = {
    ub: Math.max(0, Number.isFinite(Number(source.ub)) ? Number(source.ub) : DEFAULT_PROJECT_LEVELS.ub),
    db: Math.max(
      0,
      Number.isFinite(Number(source.db))
        ? Number(source.db)
        : Number.isFinite(Number(source.bo))
          ? Number(source.bo)
          : DEFAULT_PROJECT_LEVELS.db
    ),
    rh: Math.max(0, Number.isFinite(Number(source.rh)) ? Number(source.rh) : DEFAULT_PROJECT_LEVELS.rh),
    dp: Math.max(
      0,
      Number.isFinite(Number(source.dp))
        ? Number(source.dp)
        : Number.isFinite(Number(source.dc))
          ? Number(source.dc)
          : DEFAULT_PROJECT_LEVELS.dp
    ),
    dd: Math.max(0, Number.isFinite(Number(source.dd)) ? Number(source.dd) : DEFAULT_PROJECT_LEVELS.dd)
  };
  normalized.options = {};
  for (const key of PROJECT_LEVEL_KEYS) {
    const optionSource = sourceOptions[key] && typeof sourceOptions[key] === 'object' ? sourceOptions[key] : {};
    const includeMap = source.includeInCalculation && typeof source.includeInCalculation === 'object'
      ? source.includeInCalculation
      : {};
    const showMap = source.showIn3d && typeof source.showIn3d === 'object' ? source.showIn3d : {};
    const defaultOptions = DEFAULT_PROJECT_LEVEL_OPTIONS[key];
    const includeValue = optionSource.includeInCalculation ?? includeMap[key] ?? source[`${key}IncludeInCalculation`];
    const showValue = optionSource.showIn3d
      ?? showMap[key]
      ?? source[`${key}ShowIn3d`]
      ?? (key === 'db' ? source.floorShowIn3d : undefined)
      ?? (key === 'dd' ? source.ceilingShowIn3d : undefined);
    normalized.options[key] = {
      includeInCalculation: normalizeProjectLevelBoolean(includeValue, defaultOptions.includeInCalculation),
      showIn3d: normalizeProjectLevelBoolean(showValue, defaultOptions.showIn3d)
    };
  }
  normalized.floorGridSize = PROJECT_LEVEL_FLOOR_GRID_SIZES.includes(Number(source.floorGridSize))
    ? Number(source.floorGridSize)
    : DEFAULT_PROJECT_LEVELS.floorGridSize;
  normalized.floorColor = PROJECT_LEVEL_FLOOR_COLORS[String(source.floorColor || '').toLowerCase()]
    ? String(source.floorColor).toLowerCase()
    : DEFAULT_PROJECT_LEVELS.floorColor;
  normalized.doubleCeilingGrid = PROJECT_LEVEL_DOUBLE_CEILING_GRIDS[String(source.doubleCeilingGrid || '').toLowerCase()]
    ? String(source.doubleCeilingGrid).toLowerCase()
    : DEFAULT_PROJECT_LEVELS.doubleCeilingGrid;
  normalized.floorType = normalizeProjectLevelTypeKey(
    source.floorType ?? source.environment?.floorType,
    PROJECT_LEVEL_FLOOR_TYPES,
    DEFAULT_PROJECT_LEVELS.floorType
  );
  normalized.ceilingType = normalizeProjectLevelTypeKey(
    source.ceilingType ?? source.environment?.ceilingType,
    PROJECT_LEVEL_CEILING_TYPES,
    DEFAULT_PROJECT_LEVELS.ceilingType
  );
  normalized.environmentPreset = PROJECT_ENVIRONMENT_PRESET_KEYS.includes(String(source.environmentPreset || source.environment?.preset || '').toLowerCase())
    ? String(source.environmentPreset || source.environment?.preset).toLowerCase()
    : DEFAULT_PROJECT_LEVELS.environmentPreset;
  // Selected texture-catalog colorValues (from the "Texturen" color picker
  // catalog) — additive to floorType/ceilingType, not a replacement, so
  // existing saved projects keep rendering exactly as before until a user
  // actively picks a texture. Kept catalog-agnostic here — actual
  // resolution to a real color happens where it's consumed.
  normalized.floorTexture = normalizeProjectLevelTextureValue(source.floorTexture);
  normalized.ceilingTexture = normalizeProjectLevelTextureValue(source.ceilingTexture);
  normalized.doubleFloorTexture = normalizeProjectLevelTextureValue(source.doubleFloorTexture);
  normalized.doubleCeilingTexture = normalizeProjectLevelTextureValue(source.doubleCeilingTexture);
  const profilesSource = source.profiles && typeof source.profiles === 'object' ? source.profiles : {};
  normalized.profiles = {};
  for (const presetKey of PROJECT_ENVIRONMENT_PRESET_KEYS) {
    if (!profilesSource[presetKey] || typeof profilesSource[presetKey] !== 'object') continue;
    normalized.profiles[presetKey] = normalizeProjectLevelProfileSnapshot(profilesSource[presetKey], presetKey);
    if (presetKey !== 'outdoor') {
      normalized.profiles[presetKey].rh = normalized.rh;
    }
  }
  normalized.includeInCalculation = Object.fromEntries(PROJECT_LEVEL_KEYS.map(key => [
    key,
    normalized.options[key].includeInCalculation
  ]));
  normalized.showIn3d = Object.fromEntries(PROJECT_LEVEL_KEYS.map(key => [
    key,
    normalized.options[key].showIn3d
  ]));
  normalized.floorShowIn3d = normalized.options.db.showIn3d;
  normalized.ceilingShowIn3d = normalized.options.dd.showIn3d;
  normalized.bo = normalized.db;
  normalized.dc = normalized.dp;
  return normalized;
}

function normalizeProjectLevelProfileSnapshot(profileSource, presetKey) {
  const normalized = normalizeProjectLevels({
    ...(profileSource && typeof profileSource === 'object' ? profileSource : {}),
    profiles: null,
    environmentPreset: presetKey
  });
  delete normalized.profiles;
  return normalized;
}

function normalizeProjectLevelBoolean(value, fallback = false) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'ja', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'nein', 'off'].includes(normalized)) return false;
  }
  return Boolean(fallback);
}

function normalizeProjectLevelTypeKey(value, registry, fallback) {
  const key = String(value || '').trim().toLowerCase();
  return registry && registry[key] ? key : fallback;
}

function normalizeProjectLevelTextureValue(value) {
  if (!value || typeof value !== 'object') return null;
  const code = String(value.code || '').trim();
  if (!code) return null;
  return { ...value, code };
}

function projectLevelIncludesCalculation(levels, key) {
  const normalized = normalizeProjectLevels(levels);
  return normalized.options?.[key]?.includeInCalculation !== false;
}

function projectLevelShowsIn3D(levels, key) {
  const normalized = normalizeProjectLevels(levels);
  const options = normalized.options?.[key] || DEFAULT_PROJECT_LEVEL_OPTIONS[key];
  return options?.includeInCalculation !== false && options?.showIn3d === true;
}

function projectLevelCalculationValue(levels, key, fallback = 0) {
  const normalized = normalizeProjectLevels(levels);
  if (!projectLevelIncludesCalculation(normalized, key)) return fallback;
  const value = normalized[key];
  return Number.isFinite(Number(value)) ? Number(value) : fallback;
}

export {
  NS,
  DEFAULT_GRID_COLOR,
  CARDINALS,
  PROJECT_LEVEL_KEYS,
  PROJECT_LEVEL_FLOOR_GRID_SIZES,
  PROJECT_LEVEL_DOUBLE_CEILING_GRIDS,
  PROJECT_LEVEL_FLOOR_COLORS,
  PROJECT_LEVEL_FLOOR_TYPES,
  PROJECT_LEVEL_CEILING_TYPES,
  PROJECT_ENVIRONMENT_PRESET_KEYS,
  DEFAULT_PROJECT_LEVEL_OPTIONS,
  DEFAULT_PROJECT_LEVELS,
  GC_GALVANIZED,
  _gcParse,
  _gcHex,
  gcFill,
  gcStroke,
  gcWire,
  uid,
  clamp,
  snap,
  normalizeAngle,
  rotateWorldPoint,
  normalizeProjectLevels,
  normalizeProjectLevelProfileSnapshot,
  normalizeProjectLevelBoolean,
  normalizeProjectLevelTypeKey,
  projectLevelIncludesCalculation,
  projectLevelShowsIn3D,
  projectLevelCalculationValue
};
